'use client';

import React, { ReactNode, useState, useRef, useEffect } from 'react';
import classNames from 'classnames';
import styles from './dropdown.module.sass';

export type DropdownItemVariant = 'default' | 'danger';

export interface DropdownItem {
  label: string;
  onClick: () => void;
  icon?: ReactNode;
  variant?: DropdownItemVariant;
  disabled?: boolean;
}

export interface DropdownDivider {
  divider: true;
}

export type DropdownContent = DropdownItem | DropdownDivider;

export interface DropdownProps {
  /** Content to trigger the dropdown (usually a button) */
  trigger: ReactNode;
  /** Array of menu items or dividers */
  items: DropdownContent[];
  /** Additional CSS class for the container */
  className?: string;
  /** Additional CSS class for the menu */
  menuClassName?: string;
  /** Minimum width of dropdown menu */
  minWidth?: string;
}

const Dropdown = React.forwardRef<HTMLDivElement, DropdownProps>(
  ({ trigger, items, className, menuClassName, minWidth = '200px' }, ref) => {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const triggerRef = useRef<HTMLDivElement>(null);

    // Close dropdown when clicking outside
    useEffect(() => {
      if (!isOpen) return;

      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as Node;
        if (containerRef.current && !containerRef.current.contains(target)) {
          setIsOpen(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [isOpen]);

    // Close dropdown when pressing Escape
    useEffect(() => {
      if (!isOpen) return;

      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          setIsOpen(false);
        }
      };

      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }, [isOpen]);

    const handleTriggerClick = () => {
      setIsOpen(!isOpen);
    };

    const handleItemClick = (item: DropdownItem) => {
      if (item.disabled) return;
      item.onClick();
      setIsOpen(false);
    };

    const isDivider = (item: DropdownContent): item is DropdownDivider => {
      return 'divider' in item && item.divider;
    };

    return (
      <div
        ref={node => {
          containerRef.current = node;
          if (ref) {
            if (typeof ref === 'function') {
              ref(node);
            } else {
              ref.current = node;
            }
          }
        }}
        className={classNames(styles.container, className)}
      >
        <div
          ref={triggerRef}
          className={styles.trigger}
          onClick={handleTriggerClick}
        >
          {trigger}
        </div>

        {isOpen && (
          <div
            className={classNames(styles.menu, menuClassName, 'min-w-[var(--dropdown-min-width)]')}
            style={{ '--dropdown-min-width': minWidth } as React.CSSProperties}
          >
            {items.map((item, index) => {
              if (isDivider(item)) {
                return (
                  <div key={`divider-${index}`} className={styles.divider} />
                );
              }

              return (
                <button
                  key={`item-${index}`}
                  className={classNames(styles.item, {
                    [styles.danger]: item.variant === 'danger',
                    [styles.disabled]: item.disabled,
                  })}
                  onClick={() => handleItemClick(item)}
                  disabled={item.disabled}
                >
                  {item.icon && (
                    <span className={styles.itemIcon}>{item.icon}</span>
                  )}
                  <span>{item.label}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }
);

Dropdown.displayName = 'Dropdown';

export default Dropdown;
